import UIError from '@/errors/UIError';
import { QueryClient } from '@tanstack/react-query';
import { LoaderFunctionArgs } from 'react-router';
import { DashboardInfoResponse, DeviceResponse, PacketSortableColumn, VitalType } from './types';
import { SortDirection } from '@/utils/types';
import apiClient from '@/api';
import { queryAntennas, queryPvPanels } from './deploy-device/constants';
import { buildQueryParams } from '@/utils/build-query-params';
import { queryDeploymentTypes } from '../measurement-group/constants';
import { UpdateManifestDataResponse, UpdateSendingInterval } from '@/routes/device/update-sending-interval/types.ts';

export const DEVICE_ROOT_API_URL = '/api/v1/devices';

export const labelsByType: Record<VitalType, string> = {
	[VitalType.LithiumVoltage]: 'Lithium / SLA',
	[VitalType.AlkalineVoltage]: 'Alkaline',
	[VitalType.SolarVoltage]: 'Solar',
	[VitalType.GSMSignal]: 'RSSI',
	[VitalType.SatelliteSignal]: 'Nav Sat',
	[VitalType.Humidity]: 'Humidity'
};

export function searchDevicesPredicate(item: DeviceResponse, searchQuery: string | null | undefined): boolean {
	if (!searchQuery) {
		return true;
	}

	return Boolean(item.id.toString().includes(searchQuery) || item.site?.includes(searchQuery));
}

export function queryDashboardInfo(
	deviceId: number,
	params: {
		startDate?: string | null;
		endDate?: string | null;
		includeGainDistribution?: string | null;
	}
) {
	const queryParams = buildQueryParams(params);

	return {
		queryKey: ['devices', deviceId, 'dashboard', queryParams],
		queryFn: () =>
			apiClient.get<DashboardInfoResponse>(`${DEVICE_ROOT_API_URL}/${deviceId}/dashboard?${queryParams}`)
	};
}

export function listDevices() {
	return {
		queryKey: ['devices'],
		queryFn: () => apiClient.get(DEVICE_ROOT_API_URL)
	};
}

function queryVitals(id: number) {
	return {
		queryKey: ['devices', id, 'vitals'],
		queryFn: () => apiClient.get(`${DEVICE_ROOT_API_URL}/${id}/vitals`)
	};
}

function queryRecentPackets(id: number) {
	const fromTime = new Date();
	fromTime.setHours(fromTime.getHours() - 1);

	return {
		queryKey: ['devices', id, 'packets'],
		queryFn: () =>
			apiClient.get(`${DEVICE_ROOT_API_URL}/${id}/packets`, {
				params: {
					page: 1,
					limit: 10,
					sortDirection: SortDirection.DESC,
					sortBy: PacketSortableColumn.REALTIME,
					from: fromTime.toISOString(),
					to: new Date().toISOString()
				}
			})
	};
}

export function updateSendingInterval(payload: UpdateSendingInterval, deviceId: string) {
	return apiClient.patch<UpdateManifestDataResponse>(`${DEVICE_ROOT_API_URL}/${deviceId}/sending-interval`, payload);
}

export function getSendingInterval(deviceId: string) {
	return {
		queryKey: ['v1', 'devices', deviceId, 'sending-interval'],
		queryFn: () => apiClient.get(`${DEVICE_ROOT_API_URL}/${deviceId}/sending-interval`)
	};
}

export function loader(queryClient: QueryClient) {
	return async ({ params }: LoaderFunctionArgs) => {
		if (!params.deviceId) {
			throw new UIError('Internal Error: No Device Id provided');
		}

		const deviceId = parseInt(params.deviceId);

		if (isNaN(deviceId)) {
			throw new UIError('Internal Error: Device id format is not a number');
		}

		const recentDataQuery = queryRecentPackets(deviceId);
		const slowMonitoringDataQuery = queryVitals(deviceId);

		const antennas = queryClient.ensureQueryData(queryAntennas());
		const pvPanels = queryClient.ensureQueryData(queryPvPanels());
		const deploymentTypes = queryClient.ensureQueryData(queryDeploymentTypes());

		return {
			recentData: await queryClient.ensureQueryData(recentDataQuery),
			slowMonitoring: await queryClient.ensureQueryData(slowMonitoringDataQuery),
			devices: await queryClient.ensureQueryData(listDevices()),
			antennas,
			pvPanels,
			deploymentTypes
		};
	};
}
